import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  Collapse,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import { COLORS } from '../../utils/color';
import LanguageSelector from './LanguageSelector';
import VideoUploadZone from './VideoUploadZone';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: COLORS.gradients.background,
    borderRadius: '24px',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    margin: theme.spacing(2),
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderBottom: `1px solid ${COLORS.border.primary}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: COLORS.background.secondary,
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4),
  overflow: 'auto',
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${COLORS.border.primary}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: COLORS.background.secondary,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: COLORS.overlay.light,
    '& fieldset': {
      borderColor: COLORS.border.primary,
    },
    '&:hover fieldset': {
      borderColor: COLORS.border.focus,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: COLORS.text.secondary,
  },
  '& .MuiInputBase-input': {
    color: COLORS.text.primary,
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  ...(variant === 'primary' && {
    background: '#FFFFFF',
    color: '#000000',
    '&:hover': {
      background: '#F0F0F0',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: 'rgba(255,255,255,0.15)',
      color: 'rgba(0,0,0,0.4)',
    },
  }),
  ...(variant === 'secondary' && {
    color: COLORS.text.secondary,
    border: `1px solid ${COLORS.border.primary}`,
    '&:hover': {
      color: COLORS.text.primary,
      border: `1px solid ${COLORS.border.focus}`,
      backgroundColor: COLORS.hover.background,
    },
  }),
  ...(variant === 'danger' && {
    color: COLORS.status.error,
    border: `1px solid ${COLORS.status.error}`,
    '&:hover': {
      backgroundColor: `${COLORS.status.error}10`,
    },
  }),
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-root .Mui-completed': {
    color: COLORS.status.success,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: COLORS.text.primary,
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: COLORS.primary.main,
  },
  '& .MuiStepLabel-label': {
    color: COLORS.text.secondary,
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: COLORS.primary.main,
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: COLORS.status.success,
  },
  '& .MuiStepConnector-line': {
    borderColor: COLORS.border.primary,
  },
}));

// Video Preview Card Component
const VideoPreviewCard = styled(Box)(({ theme }) => ({
  backgroundColor: COLORS.overlay.light,
  borderRadius: '16px',
  padding: theme.spacing(3),
  border: `1px solid ${COLORS.border.primary}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const steps = ['Language Settings', 'Upload Video', 'Review & Create'];

// Helper function to get video duration using HTML5 Video API
const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Error loading video file'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

const CreateDubModal = ({ open, onClose, onSuccess }) => {
  const { showError, showSuccess } = useToast();
  const { isAdmin } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Form state
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [uploadError, setUploadError] = useState('');
  
  // Advanced settings
  const [watermark, setWatermark] = useState(false);
  const [dropBackgroundAudio, setDropBackgroundAudio] = useState(true); // Default to true for cleaner audio
  const [profanityFilter, setProfanityFilter] = useState(false);
  const [enableLipsync, setEnableLipsync] = useState(true);
  const [detectLipMovement, setDetectLipMovement] = useState(false);
  const [numSpeakers, setNumSpeakers] = useState(0);
  
  // Languages and limits
  const [languages, setLanguages] = useState({ source: [], target: [] });
  const [loadingLanguages, setLoadingLanguages] = useState(true);
  const [videoLimits, setVideoLimits] = useState(null);

  useEffect(() => {
    if (open) {
      loadLanguages();
      loadVideoLimits();
    }
  }, [open]);

  const loadLanguages = async () => {
    try {
      setLoadingLanguages(true);
      const response = await apiService.getSupportedLanguages();
      setLanguages({
        source: response.source_languages || [],
        target: response.supported_languages || []
      });
    } catch (error) {
      showError('Failed to load languages');
    } finally {
      setLoadingLanguages(false);
    }
  };

  const loadVideoLimits = async () => {
    try {
      const response = await apiService.getVideoLimits();
      
      // If backend doesn't return role-based limits, apply them on frontend
      let adjustedLimits = { ...response };
      if (isAdmin() && response.max_duration_seconds === 30) {
        // Backend returned standard limits, but user is admin - upgrade them
        adjustedLimits = {
          ...response,
          max_duration_seconds: 600,
          max_duration_minutes: 10,
          max_duration_formatted: '10 minutes'
        };
      }
      
      setVideoLimits(adjustedLimits);
    } catch (error) {
      console.error('Failed to load video limits:', error);
      // Set default limits if API fails
      const defaultDurationSeconds = isAdmin() ? 600 : 30; // 10 minutes for admin, 30 seconds for regular users
      const defaultDurationFormatted = isAdmin() ? '10 minutes' : '30 seconds';
      
      setVideoLimits({
        max_file_size_mb: 500,
        max_duration_seconds: defaultDurationSeconds,
        max_duration_minutes: defaultDurationSeconds / 60,
        max_duration_formatted: defaultDurationFormatted,
        allowed_extensions: ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv']
      });
    }
  };

  // Frontend video validation
  const validateVideoFile = async (file) => {
    try {
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > (videoLimits?.max_file_size_mb || 500)) {
        throw new Error(`File size (${fileSizeMB.toFixed(1)}MB) exceeds maximum limit of ${videoLimits?.max_file_size_mb || 500}MB`);
      }

      // Check file extension
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const allowedExtensions = videoLimits?.allowed_extensions || ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv'];
      if (!allowedExtensions.includes(fileExtension)) {
        throw new Error(`Invalid file type. Allowed formats: ${allowedExtensions.join(', ')}`);
      }

      // Check video duration with role-based limits
      const duration = await getVideoDuration(file);
      
      // Determine max duration based on user role
      let maxDurationSeconds;
      if (videoLimits?.max_duration_seconds) {
        maxDurationSeconds = videoLimits.max_duration_seconds;
      } else {
        // Fallback: Use role-based limits if API data is unavailable
        maxDurationSeconds = isAdmin() ? 600 : 30; // 10 minutes for admin, 30 seconds for regular users
      }
      
      if (duration > maxDurationSeconds) {
        const durationMinutes = duration / 60;
        const maxMinutes = maxDurationSeconds / 60;
        const userType = isAdmin() ? 'Admin' : 'Standard';
        
        if (maxDurationSeconds >= 60) {
          throw new Error(`Video duration (${durationMinutes.toFixed(1)} minutes) exceeds the maximum limit of ${maxMinutes.toFixed(1)} minutes for ${userType} users`);
        } else {
          throw new Error(`Video duration (${duration.toFixed(1)} seconds) exceeds the maximum limit of ${maxDurationSeconds} seconds for ${userType} users`);
        }
      }

      return { valid: true, duration };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!targetLanguage) {
        showError('Please select a target language');
        return;
      }
    } else if (activeStep === 1) {
      // Validate video upload
      if (!uploadedVideo) {
        showError('Please upload a video first');
        return;
      }
    }
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFileSelect = async (file) => {
    if (!file) {
      setSelectedFile(null);
      setUploadedVideo(null);
      setUploadProgress(0);
      setUploadError('');
      return;
    }

    // Clear previous errors
    setUploadError('');
    setSelectedFile(file);
    
    // Validate file before upload
    const validation = await validateVideoFile(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      setSelectedFile(file); // Keep file selected to show error
      return;
    }

    // Start upload if validation passes
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const response = await apiService.uploadVideo(file, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });
      
      setUploadedVideo(response);
      showSuccess('Video uploaded successfully!');
    } catch (error) {
      // If upload fails, clear the uploaded video but keep selected file
      setUploadedVideo(null);
      setUploadError(error.message || 'Failed to upload video');
      showError(error.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveVideo = () => {
    setSelectedFile(null);
    setUploadedVideo(null);
    setUploadProgress(0);
    setUploadError('');
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      
      // Create dubbing job
      const params = {
        target_language: targetLanguage,
        source_language: sourceLanguage,
        num_speakers: numSpeakers,
        watermark: watermark,
        drop_background_audio: dropBackgroundAudio,
        use_profanity_filter: profanityFilter,
        enable_lipsync: enableLipsync,
        detect_lip_movement: detectLipMovement
      };

      console.log('🔍 Creating dubbing job with params:', params);
      console.log('🔍 detect_lip_movement value:', detectLipMovement);

      const response = await apiService.createDubbingJob(uploadedVideo.video_id, params);
      
      showSuccess('Dubbing job created successfully!');
      
      onSuccess({
        ...response,
        project_name: uploadedVideo.filename || `Dub_${new Date().toISOString().slice(0, 10)}`,
        video_filename: uploadedVideo.filename
      });
      
      // Reset and close
      handleClose();
    } catch (error) {
      showError(error.message || 'Failed to create dubbing job');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !uploading) {
      setActiveStep(0);
      setSourceLanguage('auto');
      setTargetLanguage('');
      setSelectedFile(null);
      setUploadedVideo(null);
      setUploadProgress(0);
      setUploadError('');
      setWatermark(false);
      setDropBackgroundAudio(false);
      setProfanityFilter(false);
      setEnableLipsync(true);
      setNumSpeakers(0);
      setShowAdvanced(false);
      
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.text.primary, mb: 3 }}>
              Language Settings
            </Typography>
            
            <Grid container spacing={3}>
              
              <Grid item xs={12} md={6}>
                <LanguageSelector
                  label="Source Language"
                  value={sourceLanguage}
                  onChange={(e) => setSourceLanguage(e.target.value)}
                  languages={languages.source.filter(lang => lang.code !== 'auto')}
                  showDetect={true}
                  helperText="Language of the original video"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LanguageSelector
                  label="Target Language"
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  languages={languages.target}
                  helperText="Language to dub the video into"
                  error={!targetLanguage && activeStep > 0}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: COLORS.text.secondary,
                    '&:hover': { color: COLORS.text.primary },
                  }}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Advanced Settings
                  </Typography>
                </Box>
                
                <Collapse in={showAdvanced}>
                  <Box sx={{ mt: 2, pl: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enableLipsync}
                          onChange={(e) => setEnableLipsync(e.target.checked)}
                          sx={{ color: COLORS.text.secondary }}
                        />
                      }
                      label="Enable lip-sync (recommended)"
                      sx={{ color: COLORS.text.primary }}
                    />

                    {enableLipsync && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={detectLipMovement}
                            onChange={(e) => setDetectLipMovement(e.target.checked)}
                            sx={{ color: COLORS.text.secondary, ml: 2 }}
                          />
                        }
                        label="Detect lip movement (skip lip-sync if person not speaking)"
                        sx={{ color: COLORS.text.primary, ml: 2 }}
                      />
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dropBackgroundAudio}
                          onChange={(e) => setDropBackgroundAudio(e.target.checked)}
                          sx={{ color: COLORS.text.secondary }}
                        />
                      }
                      label="Remove background audio (recommended)"
                      sx={{ color: COLORS.text.primary }}
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={profanityFilter}
                          onChange={(e) => setProfanityFilter(e.target.checked)}
                          sx={{ color: COLORS.text.secondary }}
                        />
                      }
                      label="Apply profanity filter"
                      sx={{ color: COLORS.text.primary }}
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={watermark}
                          onChange={(e) => setWatermark(e.target.checked)}
                          sx={{ color: COLORS.text.secondary }}
                        />
                      }
                      label="Add watermark"
                      sx={{ color: COLORS.text.primary }}
                    />
                  </Box>
                </Collapse>
              </Grid>
            </Grid>
          </Box>
        );
        
      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.text.primary, mb: 3 }}>
              Upload Video
            </Typography>
            
            {videoLimits && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3,
                  backgroundColor: `${COLORS.status.info}20`,
                  color: COLORS.status.info,
                  '& .MuiAlert-icon': { color: COLORS.status.info }
                }}
              >
                Video limits ({isAdmin() ? 'Admin' : 'Standard'} user): Maximum {videoLimits.max_duration_formatted}, {videoLimits.max_file_size_mb}MB max file size
              </Alert>
            )}
            
            {/* Show video preview if video is already uploaded */}
            {uploadedVideo && !uploadError ? (
              <Box>
                <VideoPreviewCard>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ color: COLORS.text.primary, mb: 1 }}>
                      {uploadedVideo.filename}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                      Video uploaded successfully
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CheckCircleIcon sx={{ color: COLORS.status.success, fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ color: COLORS.status.success }}>
                        Ready for dubbing
                      </Typography>
                    </Box>
                  </Box>
                  <StyledButton
                    variant="danger"
                    onClick={handleRemoveVideo}
                    startIcon={<DeleteIcon />}
                    size="small"
                  >
                    Remove
                  </StyledButton>
                </VideoPreviewCard>
                
                <Alert 
                  severity="success" 
                  sx={{ 
                    mt: 3,
                    backgroundColor: `${COLORS.status.success}20`,
                    color: COLORS.status.success,
                    '& .MuiAlert-icon': { color: COLORS.status.success }
                  }}
                >
                  Video uploaded successfully! You can proceed to the next step or upload a different video.
                </Alert>
              </Box>
            ) : (
              /* Show upload zone if no video is uploaded or there's an error */
              <Box>
                <VideoUploadZone
                  onFileSelect={handleFileSelect}
                  uploadProgress={uploadProgress}
                  isUploading={uploading}
                  hasError={!!uploadError}
                  errorMessage={uploadError}
                  maxSizeMB={videoLimits?.max_file_size_mb || 500}
                  acceptedFormats={videoLimits?.allowed_extensions || ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv']}
                />
                
                {uploadError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 3,
                      backgroundColor: `${COLORS.status.error}20`,
                      color: COLORS.status.error,
                      '& .MuiAlert-icon': { color: COLORS.status.error }
                    }}
                  >
                    {uploadError}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );
        
      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.text.primary, mb: 3 }}>
              Review & Create
            </Typography>
            
            <Box sx={{ 
              backgroundColor: COLORS.overlay.light,
              borderRadius: '16px',
              padding: 3,
              border: `1px solid ${COLORS.border.primary}`,
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary }}>
                    Video File
                  </Typography>
                  <Typography variant="body1" sx={{ color: COLORS.text.primary, fontWeight: 600 }}>
                    {uploadedVideo?.filename || 'No file uploaded'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ borderColor: COLORS.border.primary }} />
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary }}>
                    Source Language
                  </Typography>
                  <Typography variant="body1" sx={{ color: COLORS.text.primary }}>
                    {sourceLanguage === 'auto' ? 'Auto-detect' : languages.source.find(l => l.code === sourceLanguage)?.name || sourceLanguage}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary }}>
                    Target Language
                  </Typography>
                  <Typography variant="body1" sx={{ color: COLORS.text.primary }}>
                    {languages.target.find(l => l.code === targetLanguage)?.name || targetLanguage}
                  </Typography>
                </Grid>
                
                {(enableLipsync || dropBackgroundAudio || profanityFilter || watermark) && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ borderColor: COLORS.border.primary }} />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary, mb: 1 }}>
                        Settings
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {enableLipsync && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: COLORS.status.success }}>
                            <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">
                              Lip-sync enabled{detectLipMovement && ' (with lip detection)'}
                            </Typography>
                          </Box>
                        )}
                        {dropBackgroundAudio && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: COLORS.text.primary }}>
                            <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">Remove background audio</Typography>
                          </Box>
                        )}
                        {profanityFilter && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: COLORS.text.primary }}>
                            <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">Profanity filter</Typography>
                          </Box>
                        )}
                        {watermark && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: COLORS.text.primary }}>
                            <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">Watermark</Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
            
            <Alert 
              severity="info" 
              sx={{ 
                mt: 3,
                backgroundColor: `${COLORS.status.info}20`,
                color: COLORS.status.info,
                '& .MuiAlert-icon': { color: COLORS.status.info }
              }}
            >
              The dubbing process may take several minutes depending on the length of your video. You'll see the progress on the dashboard.
            </Alert>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <StyledDialogContent>
        <Header>
          <Typography variant="h5" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>
            Create New Dub
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={loading || uploading}
            sx={{ color: COLORS.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Header>
        
        <Box sx={{ px: 4, pt: 3 }}>
          <StyledStepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>
        </Box>
        
        <Content>
          {loadingLanguages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderStepContent()
          )}
        </Content>
        
        <Footer>
          <StyledButton
            variant="secondary"
            onClick={activeStep === 0 ? handleClose : handleBack}
            disabled={loading || uploading}
            startIcon={activeStep > 0 && <ArrowBackIcon />}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </StyledButton>
          
          {activeStep < 2 ? (
            <StyledButton
              variant="primary"
              onClick={handleNext}
              disabled={uploading || (activeStep === 1 && !uploadedVideo)}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </StyledButton>
          ) : (
            <StyledButton
              variant="primary"
              onClick={handleCreate}
              disabled={loading || !uploadedVideo}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {loading ? 'Creating...' : 'Create Dubbing'}
            </StyledButton>
          )}
        </Footer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default CreateDubModal;